import { Inventory, War, WarStatus, BattleResult, Profile, BattleSettings, Bracket, ArmyTemplate, BattleRecord, BattleMove } from '../types';
import { supabase } from './supabaseClient';
import { CAPTURE_REWARDS } from '../utils/gameRules';

export const SupabaseService = {
  ensureGuestSession: async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (user) return;

    const { error } = await supabase.auth.signInAnonymously();
    if (error) {
      console.error("SupabaseService: Failed to create guest session", error);
    }
  },
  getProfile: async (): Promise<Profile> => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error("SupabaseService: getProfile auth getUser failed:", userError);
    }

    // Guest fallback (if not logged in)
    if (!user) {
      return {
        id: 'guest_user',
        username: 'Guest Commander',
        rating: 1000,
        gold: 500,
        is_premium: false,
        ranked_eligible: false,
        active_ranked_wars: 0,
        campaigns_completed: 0,
        consec_forfeits: 0,
        templates: [],
        inventory: { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 }
      };
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, rating, gold, is_premium, ranked_eligible, active_ranked_wars, campaigns_completed, consec_forfeits, inventory')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error("SupabaseService: Error fetching profile:", error);
      throw error;
    }

    if (!data) {
      // Create profile if missing (first login)
      const newProfile = {
        id: user.id,
        username: user.email?.split('@')[0] || `Commander-${user.id.substring(0, 8)}`,
        rating: 1000,
        gold: 500,
        is_premium: false,
        ranked_eligible: false,
        active_ranked_wars: 0,
        campaigns_completed: 0,
        consec_forfeits: 0,
        templates: [],
        inventory: { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 } // Default Starter Army
      };
      const { data: created } = await supabase.from('profiles').insert(newProfile).select().single();
      return created || newProfile as Profile;
    }

    // Fetch templates separate or join? Plan says separate table.
    const { data: templates } = await supabase.from('army_templates').select('*').eq('player_id', user.id);

    // Default defaults for new fields
    return {
      ...data,
      active_ranked_wars: data.active_ranked_wars || 0,
      ranked_eligible: data.ranked_eligible ?? false,
      campaigns_completed: data.campaigns_completed || 0,
      consec_forfeits: data.consec_forfeits || 0,
      templates: templates || [],
      inventory: data.inventory || { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 }, // Default Starter Army
      levy_campaigns_remaining: (data as any).levy_campaigns_remaining || 0
    } as Profile;
  },



  purchaseUnits: async (cost: number, items: Inventory): Promise<void> => {
    // Optimistic or atomic?
    // Atomic is safer for concurrency.
    const profile = await SupabaseService.getProfile();

    if (profile.gold < cost) throw new Error("Insufficient Gold");

    const newInventory = { ...profile.inventory };
    (Object.keys(items) as Array<keyof Inventory>).forEach(k => {
      newInventory[k] = (newInventory[k] || 0) + items[k];
    });

    await supabase.from('profiles').update({
      gold: profile.gold - cost,
      inventory: newInventory
    }).eq('id', profile.id);
  },

  // Unified Update for Barracks
  updateRoster: async (newInventory: Inventory, newGold: number): Promise<void> => {
    // Debt Limit Check (-10,000)
    if (newGold < -10000) throw new Error("Crown Debt Limit Exceeded (-10,000g)");

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error("No user");

    const { error } = await supabase.from('profiles').update({
      inventory: newInventory,
      gold: newGold
    }).eq('id', user.id);

    if (error) throw error;
  },

  sellUnits: async (refund: number, items: Inventory): Promise<void> => {
    const profile = await SupabaseService.getProfile();
    const newInventory = { ...profile.inventory };

    // Validate ownership
    let possible = true;
    (Object.keys(items) as Array<keyof Inventory>).forEach(k => {
      if ((newInventory[k] || 0) < items[k]) possible = false;
      newInventory[k] = (newInventory[k] || 0) - items[k];
    });

    if (!possible) throw new Error("Insufficient Units to Disband");

    await supabase.from('profiles').update({
      gold: profile.gold + refund,
      inventory: newInventory
    }).eq('id', profile.id);
  },

  processPrisoner: async (warId: string, type: keyof Inventory, action: 'recruit' | 'ransom', chance: number = 0): Promise<{ success: boolean, msg: string }> => {
    // Logic:
    // Recruit: Roll Dice. If Success -> Add to Profile Inventory.
    // Ransom: Guarantee. Add Gold to Profile.
    // In both cases, remove from Local "Aftermath" state? 
    // The logic here updates the SERVER state if we were tracking prisoners on the server properly.
    // Currently `TreasonPanel` manages `prisoners` from BattleResult which is local.
    // BUT we need to update the User Profile (Add Unit or Add Gold).

    const profile = await SupabaseService.getProfile();

    if (action === 'recruit') {
      const roll = Math.random();
      if (roll <= chance) {
        // Success!
        const newInv = { ...profile.inventory };
        newInv[type] = (newInv[type] || 0) + 1;

        await supabase.from('profiles').update({ inventory: newInv }).eq('id', profile.id);
        return { success: true, msg: "JOINED" };
      } else {
        return { success: false, msg: "REFUSED" };
      }
    } else {
      // Ransom
      const ransomValues: Record<string, number> = { p: 2, n: 5, b: 5, r: 15, q: 30 }; // Less than cost
      const value = ransomValues[type] || 1;

      await supabase.from('profiles').update({ gold: profile.gold + value }).eq('id', profile.id);
      return { success: true, msg: `SOLD +${value}g` };
    }
  },

  getPublicProfile: async (userId: string): Promise<Profile | null> => {
    const { data } = await supabase.from('profiles').select('id, username, rating, gold, is_premium, ranked_eligible, active_ranked_wars, campaigns_completed, consec_forfeits, inventory').eq('id', userId).maybeSingle();
    return data ? { ...data, inventory: data.inventory || { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 } } : null;
  },

  saveTemplate: async (template: Omit<ArmyTemplate, 'id'>): Promise<ArmyTemplate> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) { throw new Error("Must be logged in to save templates."); }
    await SupabaseService.getProfile();
    const { data, error } = await supabase.from('army_templates').insert({
      player_id: user.id,
      name: template.name,
      description: template.description,
      inventory: template.inventory
    }).select().single();
    if (error) throw error;
    return data;
  },

  getTemplates: async (): Promise<ArmyTemplate[]> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return [];
    const { data } = await supabase.from('army_templates').select('*').eq('player_id', user.id);
    return data || [];
  },

  updateRating: async (newRating: number): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;
    await supabase.from('profiles').update({ rating: newRating }).eq('id', user.id);
  },

  updateProfile: async (updates: Partial<Profile>): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error("Not authenticated");

    // Check uniqueness manually if username is being updated (cleaner UX than 500s)
    if (updates.username) {
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', updates.username) // Case sensitive? PG usually is.
        .neq('id', user.id) // Exclude self
        .maybeSingle();

      if (existing) throw new Error("Callsign already taken. Choose another.");
    }

    const { data, error } = await supabase.from('profiles').update(updates).eq('id', user.id).select();
    if (error) {
      console.error("SupabaseService: updateProfile error:", error);
      // Double safety for race conditions
      if (error.code === '23505') throw new Error("Callsign already taken.");
      throw error;
    }

    if (!data || data.length === 0) {
      console.error("SupabaseService: updateProfile affected 0 rows. User ID:", user.id);
      throw new Error("Update failed: No matching profile found.");
    }
  },

  checkRankedEligibility: async (): Promise<{ eligible: boolean, reason?: string }> => {
    const profile = await SupabaseService.getProfile();



    // 2. Must be solvent
    if (profile.gold <= 0) {
      return { eligible: false, reason: "Treasury bankrupt. You must abdicate." };
    }

    // 3. Explicit Flag
    if (!profile.ranked_eligible) {
      return { eligible: false, reason: "Ranked access revoked. Complete Unranked Campaign to restore." };
    }

    return { eligible: true };
  },

  abdicateRanked: async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;
    // Call Backend Function defined in SQL
    const { error } = await supabase.rpc('handle_abdication', { user_uuid: user.id });
    if (error) {
      // Fallback if RPC not exists (Dev Mode)
      console.warn("RPC handle_abdication missing, using client-side fallback.");
      await supabase.from('profiles').update({ ranked_eligible: false, gold: 0 }).eq('id', user.id);
    }
  },

  restoreRankedAccess: async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;

    const { error } = await supabase.rpc('restore_ranked_access', { user_uuid: user.id });
    if (error) {
      console.warn("RPC restore_ranked_access missing, using client-side fallback.");
      await supabase.from('profiles').update({ ranked_eligible: true, gold: 500 }).eq('id', user.id);
    }
  },

  createWar: async (inventory: Inventory, resources: number, difficulty: 'easy' | 'medium' | 'hard' = 'medium', isRanked: boolean = false, bracket?: Bracket, opponentType: 'player' | 'ai' = 'player', wagerAmount: number = 0): Promise<War | null> => {
    const { data: { user } } = await supabase.auth.getUser();


    if (!user) {
      console.error("createWar: No user/session found (Anon auth should have triggered).");
      return null;
    }

    let goldCost = 0;

    // Ranked Limits Check
    if (isRanked) {
      const profile = await SupabaseService.getProfile();

      // Check Eligibility
      if (profile.gold <= 0 || !profile.ranked_eligible) {
        // Should block, but maybe UI blocked it?
        // Double check active wars limit
      }

      // Active Ranked Limitation Removed (Tied to King Inventory)

    }

    // Standing Army Consumption (Only for Ranked)
    if (isRanked) {
      const profile = await SupabaseService.getProfile();
      const currentArmy = { ...profile.inventory };

      // Check if we have enough troops
      let insufficient = false;
      (Object.keys(inventory) as Array<keyof Inventory>).forEach(k => {
        // King is now deducted as well
        if ((currentArmy[k] || 0) < inventory[k]) insufficient = true;
        currentArmy[k] = (currentArmy[k] || 0) - inventory[k];
      });


      if (insufficient) {
        console.error("Insufficient Standing Army", inventory, currentArmy);
        return null; // Enforce strict checks for Ranked
      }

      // Commit Consumption (Deduct troops from home base)
      await supabase.from('profiles').update({ inventory: currentArmy }).eq('id', user.id);
    }

    // Consume Wagered Gold
    if (isRanked && wagerAmount > 0) {
      const profile = await SupabaseService.getProfile(); // Refresh for gold check
      if (profile.gold < wagerAmount) return null;
      await supabase.from('profiles').update({ gold: profile.gold - wagerAmount }).eq('id', user.id);
    }

    const { data, error } = await supabase.from('wars').insert({
      player_id: user.id,
      inventory,
      enemy_inventory: opponentType === 'ai' ? { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 } : { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 },
      resources_used: resources,
      status: 'ACTIVE',
      difficulty,
      is_ranked: isRanked,
      bracket_id: bracket?.id,
      gold_wagered: wagerAmount,
      battles_fought: 0,
      settings: {
        opponent_type: opponentType
      }
    }).select().single();

    if (error) {
      console.error("Failed to create war on server:", error);
      return null;
    }

    return data;
  },

  getCurrentWar: async (): Promise<War | null> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    // Fetch most recent active war where user is participant
    const { data, error } = await supabase
      .from('wars')
      .select('*')
      .or(`player_id.eq.${user.id},opponent_id.eq.${user.id}`)
      .eq('status', 'ACTIVE')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("SupabaseService: getCurrentWar failed:", error);
      throw error;
    }

    return data || null;
  },

  getWarById: async (id: string): Promise<War | null> => {
    // Validate UUID to prevent 500s
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) return null;

    const { data } = await supabase
      .from('wars')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    return data || null;
  },

  getActiveWar: async (): Promise<War | null> => {
    return SupabaseService.getCurrentWar();
  },

  updateInventory: async (warId: string, newInventory: Inventory): Promise<void> => {
    const { error } = await supabase.from('wars').update({ inventory: newInventory }).eq('id', warId);
    if (error) throw error;
  },

  updateEnemyInventory: async (warId: string, newInventory: Inventory): Promise<void> => {
    const { error } = await supabase.from('wars').update({ enemy_inventory: newInventory }).eq('id', warId);
    if (error) throw error;
  },

  updateWarStatus: async (warId: string, status: WarStatus): Promise<void> => {
    const { error } = await supabase.from('wars').update({ status }).eq('id', warId);
    if (error) throw error;


  },

  createBattle: async (warId: string, settings: BattleSettings): Promise<BattleRecord | null> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    const mergedSettings: BattleSettings = { ...settings };

    const newBattle: BattleRecord = {
      id: crypto.randomUUID(),
      war_id: warId,
      player_id: user.id,
      status: 'active',
      settings: mergedSettings,
      created_at: new Date().toISOString()
    };

    // Server Battle
    const { data, error } = await supabase.from('battles').insert({
      id: newBattle.id,
      war_id: warId,
      player_id: user.id,
      status: 'active',
      settings: mergedSettings
    }).select().single();

    if (error) {
      console.error("Failed to create battle", error);
      return null;
    }

    // Update War Status to ACTIVE so listeners trigger navigation
    const { data: war } = await supabase.from('wars').select('settings').eq('id', warId).single();
    if (war) {
      const currentWarSettings = war.settings as BattleSettings || {};
      const updatedWarSettings = { ...currentWarSettings, host_ready: false };

      await supabase.from('wars').update({
        status: 'ACTIVE',
        settings: updatedWarSettings,
        opponent_status: 'connected'
      }).eq('id', warId);
    } else {
      await supabase.from('wars').update({ status: 'ACTIVE' }).eq('id', warId);
    }

    return data;
  },

  getBattleById: async (battleId: string): Promise<BattleRecord | null> => {
    const { data } = await supabase.from('battles').select('*').eq('id', battleId).single();
    return data || null;
  },
  updateBattleSettings: async (battleId: string, settings: BattleSettings): Promise<void> => {
    await supabase.from('battles').update({ settings }).eq('id', battleId);
  },

  recordBattleResult: async (warId: string | null | undefined, result: BattleResult, battleId?: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();


    // Update the Battle Record
    if (battleId) {
      await supabase.from('battles').update({
        status: 'completed',
        result
      }).eq('id', battleId);
    }

    // Determine Player Side to resolve Winner ID correctly
    let playerSide = 'white'; // Default assumption
    let isRanked = false; // Default
    let goldWagered = 0; // Default

    if (battleId) {
      const { data: battle } = await supabase.from('battles').select('settings, war_id').eq('id', battleId).single();
      if (battle && battle.settings) {
        playerSide = battle.settings.side || 'white';
        // Check if Battle itself has matchmaking settings that imply ranked?
        // Actually, Quick Play Ranked DOES NOT have 'is_ranked' flag on Battle.
        // We might need to infer it from Settings ELO logic OR add 'is_ranked' to BattleRecord?
        // Or we just check if war_id is null. If war_id is null, we assume it MIGHT be ranked QuickPlay.
        // But how do we know if QuickPlay is Ranked or Unranked? 
        // We look at `min_elo` in settings. If > 0, it's Ranked.
        if (battle.settings.min_elo) isRanked = true;
      }
    }

    // --- WAR UPDATE LOGIC ---
    if (warId) {
      // FETCH WAR AND BATTLE FIRST
      const { data: war } = await supabase.from('wars').select('*').eq('id', warId).single();
      if (!war) return;

      isRanked = war.is_ranked || false; // Override with War setting if exists

      // Calculate New Inventories (with safety vs nulls)
      const defaultInv = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
      const newInventory = { ...defaultInv, ...(war.inventory || {}) };
      const newEnemyInventory = { ...defaultInv, ...(war.enemy_inventory || {}) };

      // 1. Apply Gains First (Promotions)
      if (result.gains) {
        (Object.keys(result.gains) as Array<keyof Inventory>).forEach(key => {
          const g = result.gains![key];
          if (g > 0) {
            newInventory[key] = (newInventory[key] || 0) + g;
            if (key !== 'p') {
              newInventory['p'] = Math.max(0, (newInventory['p'] || 0) - g);
            }
          }
        });
      }

      // 2. Apply Casualties
      (Object.keys(result.casualties) as Array<keyof Inventory>).forEach(key => {
        newInventory[key] = Math.max(0, (newInventory[key] || 0) - result.casualties[key]);
      });

      (Object.keys(result.enemy_casualties) as Array<keyof Inventory>).forEach(key => {
        newEnemyInventory[key] = Math.max(0, (newEnemyInventory[key] || 0) - result.enemy_casualties[key]);
      });

      // Determine Winner ID
      let winnerId = 'draw';
      if (result.winner !== 'draw') {
        const playerWon = (result.winner === 'w' && playerSide === 'white') || (result.winner === 'b' && playerSide === 'black');
        winnerId = playerWon ? 'player' : 'enemy';
      }

      console.log("[recordBattleResult] Updating War Inventory:", {
        warId,
        casualties: result.casualties,
        oldP: war.inventory?.p,
        newP: newInventory.p
      });

      // Update war object - Server Update
      const { error: updateError } = await supabase.from('wars').update({
        inventory: newInventory,
        enemy_inventory: newEnemyInventory,
        battles_fought: (war.battles_fought || 0) + 1,
        last_winner: winnerId
      }).eq('id', warId);

      if (updateError) console.error("Failed to update war inventory:", updateError);
    }

    // --- RANKED / PROFILE UPDATES ---
    // Applies to both War-Ranked and QuickPlay-Ranked
    if (isRanked && user) {
      let goldEarned = 0;
      (Object.keys(result.enemy_casualties) as Array<keyof Inventory>).forEach(key => {
        const count = result.enemy_casualties[key];
        const value = CAPTURE_REWARDS[key];
        goldEarned += count * value;
      });

      // Apply Royal Levy Tax (10%)
      const { data: profile } = await supabase.from('profiles').select('rating, gold').eq('id', user.id).single();

      // Update Profile (Gold & ELO)
      // Determine Winner Context again (Scoped locally if War logic skipped)
      let winnerId = 'draw';
      if (result.winner !== 'draw') {
        const playerWon = (result.winner === 'w' && playerSide === 'white') || (result.winner === 'b' && playerSide === 'black');
        winnerId = playerWon ? 'player' : 'enemy';
      }

      if (profile) {
        let newRating = profile.rating;
        if (winnerId === 'player') newRating += 25; // Use corrected winnerId
        else if (winnerId === 'enemy') newRating = Math.max(0, newRating - 25);

        await supabase.from('profiles').update({
          gold: profile.gold + goldEarned,
          rating: newRating
        }).eq('id', user.id);
      }
    }
  },

  updateWarSettings: async (warId: string, settings: BattleSettings, lastWinner?: 'player' | 'enemy' | 'draw'): Promise<void> => {
    // Server Update
    const updates: any = {};
    if (settings) updates.settings = settings;
    if (lastWinner) updates.last_winner = lastWinner;

    await supabase.from('wars').update(updates).eq('id', warId);
  },

  getAllWars: async (): Promise<War[]> => {
    // Use getSession to verify the session with the auth server.
    await SupabaseService.ensureGuestSession();
    const { data: { user } } = await supabase.auth.getUser();


    if (!user) return [];

    const { data } = await supabase
      .from('wars')
      .select('*')
      .or(`player_id.eq.${user.id},opponent_id.eq.${user.id}`)
      .order('created_at', { ascending: false });
    return data || [];
  },

  resumeWar: async (warId: string): Promise<void> => {
    await supabase.from('wars').update({ status: 'ACTIVE' }).eq('id', warId);
  },

  resignWar: async (warId: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;

    const { data: war } = await supabase.from('wars').select('player_id, opponent_id, inventory, enemy_inventory, is_ranked').eq('id', warId).single();
    if (!war) throw new Error("War not found");

    let newStatus = 'ACTIVE';

    // Determine Refund Target
    let inventoryToRefund: Inventory | null = null;
    const isPlayer = user.id === war.player_id;
    const isOpponent = user.id === war.opponent_id;

    if (isPlayer) {
      newStatus = 'DEFEAT'; // Host Resigns
      if (war.is_ranked) inventoryToRefund = war.inventory;
    } else if (isOpponent) {
      newStatus = 'VICTORY'; // Guest Resigns -> Host Wins
      if (war.is_ranked) inventoryToRefund = war.enemy_inventory;
    } else {
      throw new Error("You are not a participant in this war.");
    }

    // Process Refund if Applicable (Ranked only)
    if (inventoryToRefund) {
      const profile = await SupabaseService.getProfile();
      const newProfileInv = { ...profile.inventory };
      (Object.keys(inventoryToRefund) as Array<keyof Inventory>).forEach(k => {
        newProfileInv[k] = (newProfileInv[k] || 0) + (inventoryToRefund![k] || 0);
      });
      await supabase.from('profiles').update({ inventory: newProfileInv }).eq('id', user.id);
    }

    const { error } = await supabase.from('wars').update({ status: newStatus }).eq('id', warId);
    if (error) throw error;
  },

  getLeaderboard: async (limit: number = 100): Promise<Profile[]> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('username, rating, gold, id')
      .order('rating', { ascending: false })
      .limit(limit);

    if (error) {
      console.error("SupabaseService: getLeaderboard failed:", error);
      throw error;
    }

    if (!data) return [];

    return (data || []).map((p: any) => ({
      id: p.id,
      username: p.username || 'Unknown',
      rating: p.rating,
      gold: p.gold,
      is_premium: false, // Default
      ranked_eligible: true,
      active_ranked_wars: 0,
      campaigns_completed: 0,
      consec_forfeits: 0,
      inventory: { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 }
    } as Profile));
  },

  reset: () => {
    // No local storage to clear
  },

  subscribeToPremium: async (period: 'monthly' | 'yearly'): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error("Must be logged in to subscribe.");

    const priceId = period === 'monthly'
      ? 'price_1Sig3zAAHDDK37yWmeFjse1o'
      : 'price_1Sig5yAAHDDK37yWncHTPVZ0';

    const { data, error } = await supabase.functions.invoke('stripe-payments/create_checkout_session', {
      body: {
        price_id: priceId,
        user_id: user.id,
        return_url: window.location.origin
      }
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  },

  joinLobby: async (warId: string, inventory?: Inventory): Promise<void> => {
    // 1. Authenticate
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) throw new Error("Join Lobby: Authentication required");

    // 2. Fetch Status
    // 2. Fetch Status
    const { data: war, error: fetchError } = await supabase
      .from('wars')
      .select('status, settings, player_id, opponent_id, battles_fought')
      .eq('id', warId)
      .single();

    if (fetchError || !war) throw new Error("Join Lobby: War not found or access denied");

    // 3. Validation
    if (war.player_id === user.id) return; // User is Host

    const hasBattles = (war.battles_fought || 0) > 0;

    // Check if occupied by someone else
    if (war.opponent_id && war.opponent_id !== user.id) {
      // Only block if battles have actually occurred
      if (hasBattles) {
        throw new Error("Lobby is Locked: This war is already in progress with a different opponent.");
      }
      // Otherwise, allow overwrite (fresh lobby)
    }

    // Active War Lock check (redundant but safe fallback)
    if (war.status === 'ACTIVE' && hasBattles && war.opponent_id && war.opponent_id !== user.id) {
      throw new Error("Lobby is Locked (Active War)");
    }

    // 4. Construct Payload (Atomic Update to bypass strict RLS)
    const updates: any = {
      opponent_status: 'connected',
      opponent_id: user.id // Always assert ID claim
    };

    if (inventory) {
      updates.enemy_inventory = inventory;
    }

    // 5. Execute Update
    try {
      let query = supabase
        .from('wars')
        .update(updates)
        .eq('id', warId);

      // Only enforce status lock if battles have happened. 
      // Fresh wars are 'ACTIVE' by default but have 0 battles, so we must allow joining them.
      if (hasBattles) {
        query = query.neq('status', 'ACTIVE');
      }

      const { error: updateError } = await query;

      if (updateError) throw updateError;
    } catch (err: any) {
      if (err.code === '23505') { // Postgres Unique Violation
        console.warn("Unique Constraint Hit", err);
        throw new Error("Deployment Rejected: You are already deployed in another Active War.");
      }
      throw err;
    }
  },

  setWarRoomReady: async (warId: string, isHost: boolean, isReady: boolean): Promise<void> => {
    // 1. Fetch current settings (only needed for Host to preserve other settings)
    // For Guest, we only touch opponent_status column, so no fetch needed!
    // For Host, we touch settings column.

    if (isHost) {
      const { data: war } = await supabase.from('wars').select('settings').eq('id', warId).single();
      if (!war) return;

      const currentSettings = (war.settings || {}) as BattleSettings;
      const newSettings = { ...currentSettings, host_ready: isReady };

      await supabase.from('wars').update({ settings: newSettings }).eq('id', warId);
    } else {
      // Guest: Update opponent_status direct column.
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) throw new Error("No authenticated user");

      // Try update with validation
      const { data, error } = await supabase.from('wars').update({
        opponent_status: isReady ? 'ready' : 'connected'
      })
        .eq('id', warId)
        .eq('opponent_id', user.id)
        .select();

      if (error) throw error;

      // Handle Silent Failure (Update count 0 - User not registered as opponent)
      if (!data || data.length === 0) {
        console.warn("setWarRoomReady: Update mismatch (0 rows). Attempting Lazy Join...");

        // Check if slot is free
        const { data: warCheck } = await supabase.from('wars').select('opponent_id').eq('id', warId).single();

        if (warCheck && !warCheck.opponent_id) {
          // Slot Free -> Claim it!
          const { data: joinData, error: joinErr } = await supabase.from('wars').update({
            opponent_id: user.id,
            opponent_status: isReady ? 'ready' : 'connected'
          })
            .eq('id', warId)
            .is('opponent_id', null)
            .select();

          if (joinErr) throw joinErr;
          if (!joinData || joinData.length === 0) throw new Error("Join Failed: Opponent slot taken during negotiation.");
        } else if (warCheck && warCheck.opponent_id !== user.id) {
          throw new Error("Lobby Conflict: Opponent slot is occupied by " + warCheck.opponent_id);
        } else {
          throw new Error("Deployment Failed: Unable to update status.");
        }
      }
    }
  },

  setLobbyReady: async (warId: string, isHost: boolean, isReady: boolean): Promise<{ error: any } | null> => {
    try {
      // Use the robust column-split logic (Host -> settings, Guest -> opponent_status)
      await SupabaseService.setWarRoomReady(warId, isHost, isReady);
      return null;
    } catch (err) {
      console.error("setLobbyReady: Exception", err);
      return { error: err };
    }
  },

  getDeadKingsCount: async (warId: string, userId: string): Promise<number> => {
    const { data: battles } = await supabase
      .from('battles')
      .select('player_id, opponent_id, casualties, enemy_casualties')
      .eq('war_id', warId)
      .eq('status', 'completed');

    if (!battles) return 0;

    let deadKings = 0;
    battles.forEach((b: { player_id: string; casualties: any; opponent_id: string; enemy_casualties: any; }) => {
      if (b.player_id === userId) {
        deadKings += (b.casualties as any)?.k || 0;
      } else if (b.opponent_id === userId) {
        deadKings += (b.enemy_casualties as any)?.k || 0;
      }
    });

    return deadKings;
  },

  getBattlesByWarId: async (warId: string): Promise<BattleRecord[]> => {
    const { data } = await supabase
      .from('battles')
      .select('*')
      .eq('war_id', warId)
      .eq('status', 'completed')
      .order('created_at', { ascending: true });
    return data || [];
  },
  getActiveBattleForWar: async (warId: string): Promise<BattleRecord | null> => {
    const { data } = await supabase
      .from('battles')
      .select('*')
      .eq('war_id', warId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    return data || null;
  },
  getCampaignHistory: async (userId: string, limit: number = 20): Promise<War[]> => {
    const { data } = await supabase
      .from('wars')
      .select('*')
      .or(`player_id.eq.${userId},opponent_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(limit);
    return data || [];
  },

  getBattleHistory: async (userId?: string, limit: number = 50): Promise<BattleRecord[]> => {
    let targetId = userId;
    if (!targetId) {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return [];
      targetId = user.id;
    }

    const { data } = await supabase
      .from('battles')
      .select('*')
      .or(`player_id.eq.${targetId},opponent_id.eq.${targetId}`)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(limit);
    return data || [];
  },
  attemptTreasonGamble: async (
    warId: string,
    unitType: keyof Inventory,
    cost: number,
    successChance: number
  ): Promise<{ success: boolean; error?: string }> => {
    // 1. Check Gold
    const profile = await SupabaseService.getProfile();
    if (!profile || profile.gold < cost) return { success: false, error: "Insufficient Gold" };

    // 2. Deduct Gold (The cost of the attempt)
    const { error: goldError } = await supabase.from('profiles').update({ gold: profile.gold - cost }).eq('id', profile.id);
    if (goldError) return { success: false, error: "Treasury transaction failed" };

    // 3. RNG Roll (Server-side simulation would be better, but doing here for now)
    const roll = Math.random();
    const isSuccess = roll < successChance;

    if (isSuccess) {
      // 4. Add Unit to War Inventory
      const { data: war } = await supabase.from('wars').select('inventory').eq('id', warId).single();
      if (war) {
        const newInv = { ...war.inventory };
        newInv[unitType]++;
        await supabase.from('wars').update({ inventory: newInv }).eq('id', warId);
      }
      return { success: true };
    } else {
      return { success: false, error: "The prisoner refused your bribe and was executed." };
    }
  },

  sendBattleMove: async (battleId: string, pgn: string, history?: BattleMove[]): Promise<void> => {
    // Only update if active to prevent overwriting post-game state
    const updates: any = { pgn };
    if (history) updates.battle_history = history;

    await supabase.from('battles').update(updates).eq('id', battleId).eq('status', 'active');
  },

  // --- Quick Play / Matchmaking ---

  findQuickMatch: async (inventory: Inventory, myRating: number, isRanked: boolean, formation: number[] = []): Promise<BattleRecord | null> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    // Validate Army Requirements (Backend Guard)
    if (!inventory || (inventory.k || 0) < 1 || (inventory.p || 0) < 8) {
      console.error("Invalid Army for Quick Match: Need 1 King and 8 Pawns.");
      return null;
    }

    // Search for OPEN battles
    // status = 'searching'
    // war_id = is null
    // opponent_id = is null
    // player_id != me

    // ELO Range: +/- 200 (Simplified for MVP)
    const minRating = myRating - 200;
    const maxRating = myRating + 200;

    // Note: JSONB filtering on settings->min_elo is possible but we might just fetch and filter client side 
    // if volume is low, or use specific columns if we added them. 
    // For MVP, we'll just Grab ANY searching battle and check ELO in code to be safe.

    const { data } = await supabase
      .from('battles')
      .select('*')
      .eq('status', 'searching')
      .is('war_id', null)
      .is('opponent_id', null)
      .neq('player_id', user.id)
      .limit(10);

    if (!data) return null;

    // Filter for ELO compatibility
    const match = data.find((b: any) => {
      const settings = b.settings as BattleSettings;
      if (!settings.min_elo || !settings.max_elo) return true; // Unranked/Open
      return myRating >= settings.min_elo && myRating <= settings.max_elo;
    });

    if (match) {
      // Determine side assignment for Inventory
      const currentSettings = match.settings as BattleSettings;
      const hostSide = currentSettings.side || 'white';
      const mySide = hostSide === 'white' ? 'black' : 'white';

      const newSettings = { ...currentSettings };
      if (mySide === 'white') {
        newSettings.white_inventory = inventory;
        newSettings.white_formation = formation;
      } else {
        newSettings.black_inventory = inventory;
        newSettings.black_formation = formation;
      }

      // Found one! Claim it.
      // Atomic update to strict claim
      const { data: claimed, error } = await supabase
        .from('battles')
        .update({
          opponent_id: user.id,
          status: 'active', // Proceed to battle immediately
          settings: newSettings
        })
        .eq('id', match.id)
        .is('opponent_id', null) // CONCURRENCY CHECK
        .select()
        .single();

      if (error || !claimed) return null; // Someone else grabbed it
      return claimed;
    }

    return null;
  },

  createQuickMatch: async (inventory: Inventory, myRating: number, isRanked: boolean, formation: number[] = []): Promise<BattleRecord | null> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    // Validate Army Requirements (Backend Guard)
    if (!inventory || (inventory.k || 0) < 1 || (inventory.p || 0) < 8) {
      console.error("Invalid Army for Quick Match: Need 1 King and 8 Pawns.");
      return null;
    }

    const profile = await SupabaseService.getProfile();


    // Check Funds for Ranked? (Maybe entry fee?)
    // For now, free.

    const settings: BattleSettings = {
      side: Math.random() > 0.5 ? 'white' : 'black', // Randomize Host Side
      timeControl: '10+0',
      min_elo: isRanked ? myRating - 200 : 0,
      max_elo: isRanked ? myRating + 200 : 5000,
      white_inventory: inventory, // Placeholder, usually for Host if white
      black_inventory: inventory,  // Placeholder, usually for Host if black
      // We will refine assignments below
    };

    // We map Host Inventory to specific side later, OR we store `host_inventory`.
    // Let's use `white_inventory` / `black_inventory` and assign based on `side`.
    if (settings.side === 'white') {
      settings.white_inventory = inventory;
      settings.white_formation = formation;
      settings.black_inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 }; // Clear other side
    } else {
      settings.black_inventory = inventory;
      settings.black_formation = formation;
      settings.white_inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 }; // Clear other side
    }

    const { data, error } = await supabase.from('battles').insert({
      id: crypto.randomUUID(),
      player_id: user.id,
      war_id: null, // QUICK PLAY MARKER
      status: 'searching',
      settings
    }).select().single();

    if (error) {
      console.error("Failed to create quick match. Full Error:", JSON.stringify(error, null, 2));
      return null;
    }
    return data;
  },

  cancelQuickMatch: async (battleId: string): Promise<void> => {
    // Delete the battle only if it is still searching.
    // This prevents race condition where opponent joined just as you cancelled.
    const { error } = await supabase
      .from('battles')
      .delete()
      .eq('id', battleId)
      .eq('status', 'searching');

    if (error) console.error("Failed to cancel quick match:", error);
  },

  subscribeToBattle: (battleId: string, onUpdate: (battle: BattleRecord) => void) => {
    return supabase
      .channel(`battle-${battleId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'battles', filter: `id=eq.${battleId}` },
        (payload: any) => {
          if (payload.new) {
            onUpdate(payload.new as BattleRecord);
          }
        }
      )
      .subscribe();
  },

  requestRematch: async (battleId: string, requesterSide: 'white' | 'black'): Promise<{ success: boolean; newBattleId?: string }> => {
    try {
      // 1. Fetch Current Settings
      const { data: battle, error } = await supabase
        .from('battles')
        .select('settings, player_id, opponent_id')
        .eq('id', battleId)
        .single();

      if (error || !battle) throw error;

      const settings = battle.settings as BattleSettings;
      const updates: any = {};

      // 2. Mark Requester as Ready
      if (requesterSide === 'white') updates['rematch_requested_white'] = true;
      else updates['rematch_requested_black'] = true;

      // 3. Check for Mutual Agreement
      const whiteReady = requesterSide === 'white' || settings.rematch_requested_white;
      const blackReady = requesterSide === 'black' || settings.rematch_requested_black;

      let newBattleId: string | undefined;

      if (whiteReady && blackReady) {
        console.log("[Rematch] Both sides ready. Creating new match...");

        // 4. Create New Match
        const whiteInv = settings.white_inventory || { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const blackInv = settings.black_inventory || { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
        const hostId = battle.player_id;
        const guestId = battle.opponent_id;

        // Create new Battle Record
        // Host (player_id) becomes Black
        // Opponent (opponent_id) becomes White
        // We insert a new record. But who is "player_id"? 
        // Convention: "player_id" is the one who CREATED the record.
        // But here the server (or the second client) creates it.
        // Let's keep `player_id` as the original Host for consistency, just swap `settings.side`.

        const { data: newBattle, error: createError } = await supabase
          .from('battles')
          .insert({
            player_id: hostId,
            opponent_id: guestId,
            status: 'active', // Skip searching
            war_id: null,
            settings: {
              side: 'black', // Host plays Black
              opponent_side: 'white', // Guest plays White

              timeControl: settings.timeControl,
              min_elo: settings.min_elo,
              max_elo: settings.max_elo,

              // SWAP INVENTORIES for colors
              // white_inventory is for WHITE player.
              // black_inventory is for BLACK player.
              // If Host was White (and used white_inventory), he is now Black (and uses black_inventory).
              // So we assign his OLD inventory to the BLACK slot.
              // BUT: white_inventory should preserve the ARMY composition.
              // Wait, `white_inventory` field means "Inventory of the White Player".
              // If Host swaps to Black, their army should now be in `black_inventory`.
              // Correct.

              white_inventory: blackInv, // Guest's Army (was Black) -> Now White
              black_inventory: whiteInv, // Host's Army (was White) -> Now Black

              opponent_type: 'player'
            }
          })
          .select()
          .single();

        if (createError || !newBattle) throw createError;

        newBattleId = newBattle.id;
        updates['rematch_battle_id'] = newBattleId;
      }

      // 4. Update Old Battle Settings
      const newSettings = { ...settings, ...updates };
      await supabase
        .from('battles')
        .update({ settings: newSettings })
        .eq('id', battleId);

      return { success: true, newBattleId };

    } catch (e) {
      console.error("[SupabaseService] requestRematch failed", e);
      return { success: false };
    }
  }

};
